import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { ReactNode, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AddStudentDialog,
  BulkActionBar,
  ConfirmDialog,
  EmptyState,
  FilterPills,
  FixMissingDataDialog,
  InlineAlert,
  LoadingButton,
  PagedDataTable,
  SearchField,
  SourceBadge,
  StatusChip,
} from "src/grading/components";
import type { DataTableColumn } from "src/grading/components/DataTable/DataTable";
import type { FixMissingTarget } from "src/grading/components/FixMissingDataDialog/FixMissingDataDialog";
import {
  REFUSAL_CODES,
  RefusalCode,
  refusalCopy,
} from "src/grading/utils/apiError";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { BulkItemResult, BulkResult } from "src/models/staff";
import {
  BulkStudentAction,
  STUDENT_GRADES,
  StudentSource,
  StudentStatus,
  StudentSummary,
  studentName,
} from "src/models/students";
import {
  useBulkStudentsMutation,
  useGetMyClassesQuery,
  useGetStudentQuery,
  useListStudentsQuery,
} from "src/store/reducers/classes/classesApiSlice";
import StudentClassSelect from "./StudentClassSelect";
import StudentPanel from "./StudentPanel";
import usePeopleScope from "./usePeopleScope";
import { usePeopleUrlState } from "./usePeopleUrlState";
import { StudentClassOption } from "./useStudentClassOptions";

/** The add dialog's class field for admins: a search over every class in scope. */
const AdminClassField = ({
  districtId,
  schoolId,
  label,
  onChange,
}: {
  districtId?: string;
  schoolId?: string;
  label: string;
  onChange: (classId: string | null) => void;
}) => {
  const [value, setValue] = useState<StudentClassOption | null>(null);
  return (
    <StudentClassSelect
      ownClasses={false}
      districtId={districtId}
      schoolId={schoolId}
      label={label}
      value={value}
      onChange={(next) => {
        setValue(next);
        onChange(next?.id ?? null);
      }}
    />
  );
};

export interface StudentsTabProps {
  /** Platform admins: listed without edit actions. */
  readOnly?: boolean;
}

const SOURCES: StudentSource[] = ["roster", "manual"];
const STATUSES: StudentStatus[] = ["active", "inactive"];
const NAMED_LIMIT = 5;

const isSource = (value?: string): value is StudentSource =>
  (SOURCES as readonly string[]).includes(value ?? "");
const isStatus = (value?: string): value is StudentStatus =>
  (STATUSES as readonly string[]).includes(value ?? "");
const isGrade = (value?: string) =>
  (STUDENT_GRADES as readonly string[]).includes(value ?? "");

/**
 * Students in the viewer's scope. Teachers see their classes' students and add students to
 * them; school and district admins search every student in scope, filter, fix missing scoring
 * data and act in bulk (US3, US4).
 */
export const StudentsTab = ({ readOnly = false }: StudentsTabProps) => {
  const { t } = useTranslation();
  const enrollTitleId = useId();
  const url = usePeopleUrlState();
  const scope = usePeopleScope(url.district);
  const tableRef = useRef<HTMLDivElement>(null);
  const known = useRef(new Map<string, StudentSummary>());

  const isAdmin = scope.has(permissions.READ_SCHOOL_STUDENTS);
  const ownClasses = !isAdmin;
  const canAdd = !readOnly && scope.has(permissions.ADD_CLASS_STUDENTS);
  const canFix =
    !readOnly &&
    (scope.has(permissions.UPDATE_SCHOOL_STUDENTS) ||
      scope.has(permissions.UPDATE_CLASS_STUDENTS));
  const canBulk =
    !readOnly && isAdmin && scope.has(permissions.UPDATE_SCHOOL_STUDENTS);

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [result, setResult] = useState<ReactNode>(undefined);
  const [addOpen, setAddOpen] = useState(false);
  const [added, setAdded] = useState<string | null>(null);
  const [fixId, setFixId] = useState<string | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState({
    open: false,
    count: 0,
    names: "",
  });
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollClass, setEnrollClass] = useState<StudentClassOption | null>(
    null,
  );

  const sourceFilter = isSource(url.source) ? url.source : undefined;
  const statusFilter = isStatus(url.status) ? url.status : undefined;
  const gradeFilter = isGrade(url.grade) ? url.grade : undefined;
  const { data, isLoading, isFetching, isError, refetch } =
    useListStudentsQuery({
      districtId: scope.requestDistrictId,
      schoolId: isAdmin ? url.school : undefined,
      classId: url.class,
      gradeLevel: isAdmin ? gradeFilter : undefined,
      source: isAdmin ? sourceFilter : undefined,
      status: isAdmin ? statusFilter : undefined,
      missingScoringData: url.missing || undefined,
      search: url.q || undefined,
      limit: rowsPerPage,
      offset: url.page * rowsPerPage,
    });
  const { data: myClasses } = useGetMyClassesQuery(undefined, {
    skip: !ownClasses,
  });
  const { data: fixDetail, isError: fixLoadError } = useGetStudentQuery(
    fixId ?? "",
    { skip: !fixId },
  );
  const [bulkStudents, { isLoading: bulkRunning }] = useBulkStudentsMutation();

  const rows = data?.items ?? [];
  rows.forEach((student) => known.current.set(student.id, student));
  const teacherClasses = (myClasses ?? []).filter((c) => c.active !== false);

  const filtered = Boolean(
    url.q ||
      url.class ||
      url.missing ||
      (isAdmin && (url.school || url.grade || url.source || url.status)),
  );

  const nameOf = (id: string) => {
    const student = known.current.get(id);
    return student ? studentName(student) : id;
  };
  const selectedNames = selectedIds.map(nameOf);
  const namesText =
    selectedNames.length > NAMED_LIMIT
      ? t("people.andMore", {
          names: selectedNames.slice(0, NAMED_LIMIT).join(", "),
          count: selectedNames.length - NAMED_LIMIT,
        })
      : selectedNames.join(", ");

  const fixTarget: FixMissingTarget | null =
    fixId && fixDetail && fixDetail.id === fixId
      ? {
          id: fixDetail.id,
          firstName: fixDetail.firstName,
          lastName: fixDetail.lastName,
          missingSex: !fixDetail.benchmarkSex,
          missingAge: fixDetail.ageYears === null,
        }
      : null;

  const describe = (items: BulkItemResult[]) => {
    const refused = items.filter((item) => item.status !== "ok");
    const updated = items.length - refused.length;
    return (
      <>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {t("people.students.bulk.result", {
            updated,
            skipped: refused.length,
          })}
        </Box>
        {refused.length ? (
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {refused.map((item) => {
              const code = (REFUSAL_CODES as readonly string[]).includes(
                item.code ?? "",
              )
                ? (item.code as RefusalCode)
                : undefined;
              return (
                <li key={item.id}>
                  {t("people.students.bulk.reason", {
                    name: nameOf(item.id),
                    reason: code
                      ? refusalCopy(t, code)
                      : (item.message ?? t("people.students.bulk.notChanged")),
                  })}
                </li>
              );
            })}
          </Box>
        ) : null}
      </>
    );
  };

  /**
   * Runs a bulk action. Deactivate and reactivate apply to school-added students only, so
   * rostered rows are skipped here and reported with the roster explanation (FR-020).
   */
  const runBulk = async (action: BulkStudentAction, classId?: string) => {
    setResult(undefined);
    const rosterOnly = action !== "enroll";
    const skipped: BulkItemResult[] = rosterOnly
      ? selectedIds
          .filter((id) => known.current.get(id)?.source === "roster")
          .map((id) => ({ id, status: "refused", code: "ROSTER_MANAGED" }))
      : [];
    const ids = selectedIds.filter(
      (id) => !skipped.some((item) => item.id === id),
    );
    try {
      const outcome: BulkResult = ids.length
        ? await bulkStudents({
            action,
            ids,
            ...(classId ? { classId } : {}),
          }).unwrap()
        : { results: [] };
      setSelectedIds([]);
      setResult(describe([...outcome.results, ...skipped]));
    } catch {
      setResult(t("people.students.bulk.actionError"));
    }
  };

  const deactivate = async () => {
    await runBulk("deactivate");
    setConfirmDeactivate((current) => ({ ...current, open: false }));
  };

  const enroll = async () => {
    if (!enrollClass) return;
    await runBulk("enroll", enrollClass.id);
    setEnrollOpen(false);
    setEnrollClass(null);
  };

  const addButton =
    canAdd && (isAdmin || teacherClasses.length > 0) ? (
      <Button variant="contained" onClick={() => setAddOpen(true)}>
        {t("people.students.add")}
      </Button>
    ) : null;

  const columns: DataTableColumn<StudentSummary>[] = [
    {
      key: "name",
      header: t("people.students.columns.name"),
      render: (student) => (
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => url.setFilters({ student: student.id })}
            sx={{
              p: 0,
              minWidth: 0,
              justifyContent: "flex-start",
              fontSize: 14,
              fontWeight: 800,
              color: "text.primary",
              textAlign: "left",
            }}
          >
            {studentName(student)}
          </Button>
          {student.externalId ? (
            <Box
              component="span"
              sx={{ fontSize: 13, color: "text.secondary" }}
            >
              {t("people.students.sisLine", { id: student.externalId })}
            </Box>
          ) : null}
        </Box>
      ),
    },
    {
      key: "grade",
      header: t("people.students.columns.grade"),
      render: (student) => student.gradeLevel ?? "—",
    },
    ...(isAdmin
      ? [
          {
            key: "school",
            header: t("people.students.columns.school"),
            render: (student: StudentSummary) => student.schoolName ?? "—",
          },
        ]
      : []),
    {
      key: "classes",
      header: t("people.students.columns.classes"),
      render: (student) =>
        student.classes.length
          ? student.classes.map((c) => c.name).join(", ")
          : t("people.students.noClasses"),
    },
    {
      key: "source",
      header: t("people.students.columns.source"),
      render: (student) => <SourceBadge source={student.source} />,
    },
    {
      key: "status",
      header: t("people.students.columns.status"),
      render: (student) => (
        <StatusChip status={student.active ? "active" : "deactivated"} />
      ),
    },
    ...(canFix
      ? [
          {
            key: "actions",
            header: t("people.students.columns.actions"),
            align: "right" as const,
            render: (student: StudentSummary) =>
              student.missingScoringData ? (
                <Button
                  size="small"
                  variant="outlined"
                  aria-label={t("people.students.fixLabel", {
                    name: studentName(student),
                  })}
                  onClick={() => setFixId(student.id)}
                >
                  {t("people.students.fix")}
                </Button>
              ) : null,
          },
        ]
      : []),
  ];

  const emptyState = filtered ? (
    <EmptyState
      headingLevel={3}
      title={t("people.noMatchTitle")}
      body={t("people.noMatchBody")}
      action={
        <Button variant="outlined" onClick={url.clearFilters}>
          {t("people.clearFilters")}
        </Button>
      }
    />
  ) : (
    <EmptyState
      headingLevel={3}
      title={t("people.students.emptyTitle")}
      body={t("people.students.emptyBody")}
      action={addButton}
    />
  );

  const select = (
    label: string,
    value: string | undefined,
    key: "school" | "class" | "grade" | "source" | "status",
    allLabel: string,
    options: { value: string; label: string }[],
  ) => (
    <TextField
      select
      size="small"
      label={label}
      value={value ?? ""}
      onChange={(event) =>
        url.setFilters({ [key]: event.target.value || null })
      }
      sx={{ minWidth: 150 }}
    >
      <MenuItem value="">{allLabel}</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Box>
      <Stack
        component="section"
        aria-label={t("people.toolbar")}
        direction="row"
        flexWrap="wrap"
        useFlexGap
        spacing={1.5}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <SearchField
          label={t("people.students.searchLabel")}
          value={url.q}
          onChange={(q) => url.setFilters({ q })}
          sx={{ width: { xs: "100%", sm: 280 } }}
        />
        {ownClasses && teacherClasses.length > 1
          ? select(
              t("people.students.filters.class"),
              url.class,
              "class",
              t("people.students.filters.allClasses"),
              teacherClasses.map((c) => ({ value: c.id, label: c.name })),
            )
          : null}
        {isAdmin && scope.schools.length > 0
          ? select(
              t("people.students.filters.school"),
              url.school,
              "school",
              t("people.students.filters.allSchools"),
              scope.schools.map((s) => ({ value: s.id, label: s.name })),
            )
          : null}
        {isAdmin ? (
          <>
            {select(
              t("people.students.filters.grade"),
              gradeFilter,
              "grade",
              t("people.students.filters.allGrades"),
              STUDENT_GRADES.map((g) => ({ value: g, label: g })),
            )}
            {select(
              t("people.students.filters.source"),
              sourceFilter,
              "source",
              t("people.students.filters.allSources"),
              SOURCES.map((s) => ({
                value: s,
                label: t(`people.source.${s}`),
              })),
            )}
            {select(
              t("people.students.filters.status"),
              statusFilter,
              "status",
              t("people.students.filters.allStatuses"),
              [
                {
                  value: "inactive",
                  label: t("people.students.filters.inactive"),
                },
              ],
            )}
          </>
        ) : null}
        <FilterPills
          size="sm"
          aria-label={t("people.students.filters.scoringData")}
          value={url.missing ? "missing" : "all"}
          onChange={(next) => url.setFilters({ missing: next === "missing" })}
          options={[
            { value: "all", label: t("people.students.filters.everyone") },
            {
              value: "missing",
              label: t("people.students.filters.missing"),
            },
          ]}
        />
        {addButton ? <Box sx={{ ml: { sm: "auto" } }}>{addButton}</Box> : null}
      </Stack>

      {added ? (
        <Box sx={{ mb: 1.5 }}>
          <InlineAlert severity="success">
            {t("people.students.added", { name: added })}
          </InlineAlert>
        </Box>
      ) : null}
      {fixId && fixLoadError ? (
        <Box sx={{ mb: 1.5 }}>
          <InlineAlert severity="error">
            {t("student.fix.loadError")}
          </InlineAlert>
        </Box>
      ) : null}

      <BulkActionBar
        count={selectedIds.length}
        onClear={() => setSelectedIds([])}
        result={result}
        returnFocusTo={() =>
          tableRef.current?.querySelector<HTMLElement>('input[type="checkbox"]')
        }
        actions={
          canBulk ? (
            <>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                disabled={bulkRunning}
                onClick={() => setEnrollOpen(true)}
              >
                {t("people.students.bulk.enroll")}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                disabled={bulkRunning}
                onClick={() =>
                  setConfirmDeactivate({
                    open: true,
                    count: selectedIds.length,
                    names: namesText,
                  })
                }
              >
                {t("people.students.bulk.deactivate")}
              </Button>
              <LoadingButton
                size="small"
                variant="outlined"
                color="inherit"
                loading={bulkRunning && !confirmDeactivate.open && !enrollOpen}
                disabled={bulkRunning}
                onClick={() => runBulk("reactivate")}
                label={t("people.students.bulk.reactivate")}
              />
            </>
          ) : undefined
        }
      />

      {isError ? (
        <InlineAlert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {t("common.retry")}
            </Button>
          }
        >
          {t("people.students.loadError")}
        </InlineAlert>
      ) : (
        <Box ref={tableRef}>
          <PagedDataTable<StudentSummary>
            aria-label={t("people.students.tableLabel")}
            columns={columns}
            rows={rows}
            getRowKey={(student) => student.id}
            getRowLabel={studentName}
            loading={isLoading || (isFetching && rows.length === 0)}
            total={data?.total ?? 0}
            page={url.page}
            rowsPerPage={rowsPerPage}
            onPageChange={(page) => url.setFilters({ page })}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              url.setFilters({ page: 0 });
            }}
            selectable={canBulk}
            selectedIds={selectedIds}
            onSelectionChange={(ids) => setSelectedIds(ids.map(String))}
            emptyState={emptyState}
          />
        </Box>
      )}

      <ConfirmDialog
        open={confirmDeactivate.open}
        tone="destructive"
        title={t("people.students.bulk.deactivateTitle", {
          count: confirmDeactivate.count,
        })}
        body={t("people.students.bulk.deactivateBody", {
          names: confirmDeactivate.names,
        })}
        confirmLabel={t("people.students.bulk.deactivate")}
        loading={bulkRunning}
        onConfirm={deactivate}
        onClose={() =>
          setConfirmDeactivate((current) => ({ ...current, open: false }))
        }
      />

      <Dialog
        open={enrollOpen}
        onClose={bulkRunning ? undefined : () => setEnrollOpen(false)}
        aria-labelledby={enrollTitleId}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id={enrollTitleId} component="h2">
          {t("people.students.bulk.enrollTitle")}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <DialogContentText>
            {t("people.students.bulk.enrollBody")} {namesText}
          </DialogContentText>
          <StudentClassSelect
            ownClasses={false}
            districtId={scope.requestDistrictId}
            schoolId={url.school}
            label={t("people.students.classPicker.label")}
            value={enrollClass}
            onChange={setEnrollClass}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            color="inherit"
            disabled={bulkRunning}
            onClick={() => setEnrollOpen(false)}
          >
            {t("common.cancel")}
          </Button>
          <LoadingButton
            variant="contained"
            loading={bulkRunning}
            disabled={!enrollClass}
            onClick={enroll}
            label={t("people.students.bulk.enrollSubmit")}
          />
        </DialogActions>
      </Dialog>

      {canAdd ? (
        <AddStudentDialog
          open={addOpen}
          onClose={() => setAddOpen(false)}
          classes={
            ownClasses
              ? teacherClasses.map((c) => ({ id: c.id, name: c.name }))
              : undefined
          }
          onAdded={setAdded}
          renderClassPicker={
            ownClasses
              ? undefined
              : (onChange) => (
                  <AdminClassField
                    districtId={scope.requestDistrictId}
                    schoolId={url.school}
                    label={t("student.add.class")}
                    onChange={onChange}
                  />
                )
          }
        />
      ) : null}

      <FixMissingDataDialog
        student={fixTarget}
        onClose={() => setFixId(null)}
      />

      {url.student ? (
        <StudentPanel
          studentId={url.student}
          ownClasses={ownClasses}
          readOnly={readOnly}
          districtId={scope.requestDistrictId}
          onClose={() => url.setFilters({ student: null })}
        />
      ) : null}
    </Box>
  );
};

export default StudentsTab;
